// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GoAction, RolesService } from '@app/core';
import { AlertService } from '@app/core/services/alert.service';
import { Store } from '@ngrx/store';
import { BehaviorSubject, concat, Observable, of, Subject, zip } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-project-committee-handler',
  templateUrl: './project-comittee-handler.component.html',
  styleUrls: ['./project-comittee-handler.component.scss']
})
export class ProjectCommitteeHandlerComponent implements OnInit {
  @Input() usernames: string[] = [];
  @Input() roleId = '';
  @Input() backTo = [];
  @Output() saved = new EventEmitter<boolean>();

  projects$: Observable<SelectItem[]> = of([]);
  projectLoading = false;
  projectInput$ = new Subject<string>();

  selectedProject$ = new BehaviorSubject<any>(null);

  committees: any[] = [];
  committees$: Observable<SelectItem[]> = of([]);
  selectedCommittees: any[] = [];

  CommitteesPlaceholder = 'Select Committees';

  addRoleLoading = false;

  constructor(private rolesService: RolesService, private alertService: AlertService, private store: Store<any>) {}

  ngOnInit() {
    this.loadProjects();
    this.loadCommittees();
  }

  trackByFn(item: SelectItem) {
    return item.id;
  }

  loadProjects() {
    this.projects$ = concat(
      of([]),
      this.projectInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.projectLoading = true)),
        switchMap(term =>
          this.rolesService
            .getProjects({
              search: term
            })
            .pipe(
              catchError(() => of([])),
              tap(() => (this.projectLoading = false)),
              tap(() => (this.selectedCommittees = []))
            )
        )
      )
    );
  }

  loadCommittees() {
    this.committees$ = this.selectedProject$.pipe(
      switchMap(project => {
        if (!project || !project.id) {
          return of([]);
        }

        return this.rolesService.getProjectScopes({
          projectId: project.id
        });
      }),
      map(scopes => {
        if (!scopes || !scopes.length) {
          this.CommitteesPlaceholder = 'No Committees found';
          return [];
        } else {
          this.CommitteesPlaceholder = 'Select Committees';
        }

        return scopes.map(scope => {
          return {
            id: scope.id,
            name: scope.name
          };
        });
      })
    );
  }

  save() {
    if (this.selectedCommittees.length === 0) {
      this.alertService.error('Please select at least one committee');
      return;
    }

    if (this.addRoleLoading) {
      this.alertService.error('Please wait for the current operation to complete');
      return;
    }

    zip(this.selectedProject$, of(this.selectedCommittees)).subscribe(([project, committees]) => {
      console.log('saved', { project, committees });
      const projectId = project.id;
      const committeeIds = committees.map(c => c.id);

      this.rolesService
        .createRoleMeetingCommittees({
          roleId: this.roleId,
          usernames: this.usernames,
          projectId,
          committeeIds
        })
        .pipe(
          tap(() => (this.addRoleLoading = true)),
          catchError(err => {
            this.alertService.error(err.error.message);
            throw err;
          }),
          tap(() => (this.addRoleLoading = false))
        )
        .subscribe(response => {
          console.log('saved', response);
          this.alertService.success('Role scope saved successfully');
          this.saved.emit(true);
          this.backToPreviousPage();
        });
    });
  }

  cancel() {
    this.backToPreviousPage();
    this.saved.emit(false);
  }

  backToPreviousPage() {
    if (this.backTo.length) {
      this.store.dispatch(new GoAction(this.backTo));
    }
  }
}

interface SelectItem {
  id: string;
  name: string;
}



// WEBPACK FOOTER //
// ./src/app/access-management/project-comittee-handler/project-comittee-handler.component.ts